import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Play } from 'lucide-react';
import { bookingsService, type BookingDto } from '@/services/bookings';
import { scheduleService } from '@/services/schedule';
import { useToast } from '@/components/ui/use-toast';
import {
  PromoCard,
  PromoCardBody,
  PromoCardDesc,
  PromoCardHeader,
  PromoCardTitle,
} from '@/components/promo/PromoCard';
import MonthCalendar, { type DayBookingBlob } from '@/components/promo/MonthCalendar';
import AvailabilityPanel from '@/components/promo/AvailabilityPanel';
import ZoomMeetingEmbed from '@/components/promo/ZoomMeetingEmbed';
import {
  formatInTimezone,
  formatTimeRange,
  getBrowserTimezone,
  utcDateKey,
} from '@/utils/datetime';

const TeacherBookingsPage: React.FC = () => {
  const { toast } = useToast();
  const tz = getBrowserTimezone();
  const [bookings, setBookings] = useState<BookingDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [blocksLoading, setBlocksLoading] = useState(true);
  const [scheduleBlocks, setScheduleBlocks] = useState<Awaited<ReturnType<typeof scheduleService.list>>>([]);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [meetBooking, setMeetBooking] = useState<BookingDto | null>(null);
  const [joining, setJoining] = useState(false);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await bookingsService.getBookings();
      setBookings(Array.isArray(data) ? data : []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load bookings', variant: 'destructive' });
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const loadBlocks = async () => {
    try {
      setBlocksLoading(true);
      setScheduleBlocks(await scheduleService.list());
    } catch {
      setScheduleBlocks([]);
    } finally {
      setBlocksLoading(false);
    }
  };

  useEffect(() => {
    void loadBookings();
    void loadBlocks();
  }, []);

  const bookingsByDayKey = useMemo(() => {
    const map = new Map<string, DayBookingBlob[]>();
    bookings.forEach((b) => {
      const key = utcDateKey(b.startTime, tz);
      const list = map.get(key) || [];
      list.push({ id: b.id, title: b.studentName || b.title, status: b.status });
      map.set(key, list);
    });
    return map;
  }, [bookings, tz]);

  const selectedKey = utcDateKey(selectedDay.toISOString(), tz);
  const dayBookings = useMemo(
    () =>
      bookings
        .filter((b) => utcDateKey(b.startTime, tz) === selectedKey)
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()),
    [bookings, selectedKey, tz]
  );

  const nextUpcoming = useMemo(() => {
    const now = Date.now();
    return [...bookings]
      .filter((b) => new Date(b.endTime).getTime() > now && b.status !== 'cancelled')
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0];
  }, [bookings]);

  const whenLabel = formatInTimezone(selectedDay.toISOString(), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const handleJoin = async (booking: BookingDto) => {
    setJoining(true);
    try {
      let active = booking;
      if (active.status !== 'confirmed') {
        active = await bookingsService.updateBookingStatus(booking.id, 'confirmed');
        await loadBookings();
      }
      if (!active.meetingJoinUrl) {
        toast({
          title: 'No meeting link',
          description: 'Meeting URL is not available for this booking.',
          variant: 'destructive',
        });
        return;
      }
      setMeetBooking(active);
    } catch {
      toast({ title: 'Error', description: 'Could not start meeting', variant: 'destructive' });
    } finally {
      setJoining(false);
    }
  };

  const addBlock = async (body: {
    dayOfWeek: number;
    startLocal: string;
    endLocal: string;
    label?: string;
  }) => {
    await scheduleService.create({ ...body, teacherTimezone: tz });
    await loadBlocks();
    toast({ title: 'Block added', description: 'Those times are now unavailable for booking.' });
  };

  return (
    <div className="space-y-4">
      <div className="book-layout">
        <PromoCard className="book-layout-main">
          <PromoCardHeader>
            <PromoCardTitle icon={<CalendarDays size={20} strokeWidth={2} />}>Bookings</PromoCardTitle>
            <PromoCardDesc>
              Month view · all times in your timezone ({tz}). Student times adjust automatically on
              their devices.
            </PromoCardDesc>
          </PromoCardHeader>
          <PromoCardBody>
            <div className="book-toolbar">
              <button type="button" className="pill-btn" onClick={() => {
                const t = new Date();
                setViewDate(t);
                setSelectedDay(t);
              }}>
                Today
              </button>
            </div>
            <MonthCalendar
              year={year}
              month={month}
              selected={selectedDay}
              bookingsByDayKey={bookingsByDayKey}
              timeZone={tz}
              onSelectDay={(d) => {
                setSelectedDay(d);
                setViewDate(d);
              }}
              onPrevMonth={() => setViewDate(new Date(year, month - 1, 1))}
              onNextMonth={() => setViewDate(new Date(year, month + 1, 1))}
            />
            <div className="day-section">
              <div className="when">{whenLabel}</div>
              {loading ? (
                <p className="promo-empty">Loading…</p>
              ) : dayBookings.length === 0 ? (
                <p className="promo-empty">No bookings this day</p>
              ) : (
                dayBookings.map((b) => (
                  <div key={b.id} className="booking-tile" style={{ marginBottom: 8 }}>
                    <div>
                      <div className="t1">{b.studentName || b.title}</div>
                      <div className="t2">
                        {formatTimeRange(b.startTime, b.endTime, tz)}
                        {b.studentTimezone && b.studentTimezone !== tz
                          ? ` · student: ${formatInTimezone(b.startTime, { hour: 'numeric', minute: '2-digit', hour12: true }, b.studentTimezone)} (${b.studentTimezone})`
                          : ''}
                      </div>
                    </div>
                    <span className={`badge ${b.status === 'confirmed' ? 'confirmed' : ''}`}>
                      {b.status.toUpperCase()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </PromoCardBody>
        </PromoCard>

        <PromoCard className="book-layout-side">
          <PromoCardBody>
            <AvailabilityPanel
              blocks={scheduleBlocks}
              loading={blocksLoading}
              onAdd={addBlock}
              onRemove={async (id) => {
                await scheduleService.remove(id);
                await loadBlocks();
              }}
            />
          </PromoCardBody>
        </PromoCard>
      </div>

      <PromoCard>
        <PromoCardHeader>
          <PromoCardTitle icon={<Play size={20} strokeWidth={2} />}>Upcoming lesson</PromoCardTitle>
          <PromoCardDesc>Join opens an embedded Zoom session in the app.</PromoCardDesc>
        </PromoCardHeader>
        <PromoCardBody>
          <div className="live-card">
            <div className="live-ico">
              <Play size={22} color="#059669" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>
                {nextUpcoming?.studentName || nextUpcoming?.title || 'No upcoming lesson'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                {nextUpcoming
                  ? formatTimeRange(nextUpcoming.startTime, nextUpcoming.endTime, tz)
                  : 'Schedule or confirm a booking to join'}
              </div>
            </div>
            <button
              type="button"
              className="join-btn"
              disabled={!nextUpcoming || joining}
              onClick={() => nextUpcoming && void handleJoin(nextUpcoming)}
            >
              {joining ? 'Starting…' : 'Join meeting'}
            </button>
          </div>
          {meetBooking?.meetingJoinUrl && (
            <ZoomMeetingEmbed
              joinUrl={meetBooking.meetingJoinUrl}
              title={meetBooking.title}
              password={meetBooking.meetingPassword}
              onLeave={() => setMeetBooking(null)}
            />
          )}
        </PromoCardBody>
      </PromoCard>
    </div>
  );
};

export default TeacherBookingsPage;
